export interface EmailTemplateColors {
  primary?: string;
  accent?: string;
  text?: string;
  background?: string;
  headerColor?: string;
  sectionBackground?: string;
}

export type GoogleFont = "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";

export interface EmailTemplateFonts {
  family?: string;
  headingSize?: number;
  googleFont?: GoogleFont;
}

export interface EmailTemplateSections {
  showMetrics?: boolean;
  showLeadInfo?: boolean;
  showTopics?: boolean;
  showInsights?: boolean;
  showTranscript?: boolean;
  showSummary?: boolean;
}

export interface EmailTemplateBranding {
  logoUrl?: string;
  companyName?: string;
}

export interface EmailTemplateButtons {
  buttonStyle?: 'rounded' | 'square' | 'pill';
  borderRadius?: number;
  shadowEnabled?: boolean;
  buttonText?: string;
}

// --- V2 Block-Based Model ---

export type ContentBlockType =
  // Conversation report — original blocks
  | 'metrics'
  | 'lead_info'
  | 'topics'
  | 'insights'
  | 'transcript'
  | 'summary'
  | 'mobile_app_promo'
  // Conversation report — value-layer blocks (auto-injected by renderer)
  | 'goal_funnel'
  | 'next_steps'
  | 'top_call_comparison'
  | 'suggested_follow_up'
  | 'week_in_review'
  // Customer report — value-layer blocks (auto-injected by renderer)
  | 'get_more_help'
  | 'key_takeaways'
  | 'highlights'
  | 'useful_links'
  | 'time_saved'
  // Meeting recap — blocks
  | 'attendees'
  | 'key_insights'
  | 'decisions'
  | 'action_plan'
  | 'recommendations'
  | 'sentiment'
  // Escalation alert — near-copy of conversation report blocks plus urgency
  | 'urgency_banner'
  | 'escalation_reason'
  // Conversation digest — genuinely new block set (aggregate period summary)
  | 'digest_summary'
  | 'digest_conversation_list'
  | 'digest_escalations'
  | 'digest_sentiment_breakdown'
  | 'digest_topics'
  // Conversation auto digest — genuinely new block set (3-category AI decision summary)
  | 'auto_digest_summary'
  | 'auto_digest_needs_attention'
  | 'auto_digest_auto_handled'
  | 'auto_digest_irrelevant'
  | 'auto_digest_recommendations'
  // View report — genuinely new block set (scheduled Inbox View data digest)
  | 'report_data_summary'
  | 'report_key_takeaways'
  | 'report_flagged_rows'
  // User-addable custom blocks
  | 'custom_text'
  | 'cta_button'
  | 'divider'
  | 'image'
  // Branded email signature — shared with HG Mail's reply footer, driven by
  // GenieBranding.signatureStyle (src/types/ui/branding.ts)
  | 'signature';

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

export interface EmailTemplateConfigV1 {
  version?: 1;
  colors?: EmailTemplateColors;
  fonts?: EmailTemplateFonts;
  sections?: EmailTemplateSections;
  branding?: EmailTemplateBranding;
  buttons?: EmailTemplateButtons;
}

export interface EmailTemplateConfigV2 {
  version: 2;
  colors?: EmailTemplateColors;
  fonts?: EmailTemplateFonts;
  branding?: EmailTemplateBranding;
  buttons?: EmailTemplateButtons;
  blocks: ContentBlock[];
}

export type EmailTemplateConfig = EmailTemplateConfigV1 | EmailTemplateConfigV2;

export type TemplateConfig = EmailTemplateConfig;

export type EmailTemplateType =
  | 'conversation_report'
  | 'customer_report'
  | 'meeting_recap'
  | 'escalation_alert'
  | 'conversation_digest'
  | 'conversation_auto_digest'
  | 'view_report';

// --- Block Defaults & Migration ---

// Per-template default labels. Several block types are shared between the two
// reports but render under different headings (e.g. `topics` is "Main Topics"
// in the conversation report but "Topics Covered" in the customer report).
// Always resolve labels via `getDefaultBlockLabel(type, templateType)`.

const CONVERSATION_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  metrics: 'Key Performance Metrics',
  lead_info: 'Goals Information',
  topics: 'Main Topics',
  insights: 'Key Insights',
  transcript: 'Conversation Transcript',
  summary: 'Conversation Summary',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
  goal_funnel: 'Goal Funnel',
  next_steps: 'Next Steps',
  top_call_comparison: 'Best Call This Week',
  suggested_follow_up: 'Suggested Follow-Up',
  week_in_review: 'This Week',
};

const CUSTOMER_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  summary: 'What We Covered',
  mobile_app_promo: 'Help Genie in your pocket',
  topics: 'Topics Covered',
  key_takeaways: 'Key Takeaways',
  highlights: 'Memorable Moments',
  next_steps: "What's Next",
  useful_links: 'Useful Links',
  time_saved: 'Time Saved',
  get_more_help: 'Save This Genie',
  signature: 'Email Signature',
};

const MEETING_RECAP_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  summary: 'Meeting Summary',
  attendees: 'Attendees',
  key_takeaways: 'Key Takeaways',
  key_insights: 'Key Insights',
  decisions: 'Decisions Made',
  action_plan: 'Action Items',
  recommendations: 'Recommendations',
  sentiment: 'Meeting Sentiment',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
};

const ESCALATION_ALERT_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  urgency_banner: 'Urgency Banner',
  escalation_reason: 'Escalation Reason',
  metrics: 'Key Performance Metrics',
  lead_info: 'Insights',
  topics: 'Main Topics',
  insights: 'Key Points',
  transcript: 'Conversation Transcript',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
};

const CONVERSATION_DIGEST_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  digest_summary: 'Period Summary',
  digest_sentiment_breakdown: 'Sentiment Breakdown',
  digest_topics: 'Top Topics',
  digest_conversation_list: 'Conversations This Period',
  digest_escalations: 'Escalations This Period',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
};

const CONVERSATION_AUTO_DIGEST_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  auto_digest_summary: 'Period Summary',
  auto_digest_needs_attention: 'Needs Your Attention',
  auto_digest_auto_handled: 'Handled Automatically',
  auto_digest_irrelevant: 'Not Relevant / Informal',
  auto_digest_recommendations: 'Recommendations',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
};

const VIEW_REPORT_BLOCK_LABELS: Partial<Record<ContentBlockType, string>> = {
  report_data_summary: 'Report Summary',
  report_key_takeaways: 'Key Takeaways',
  report_flagged_rows: 'Rows to Pay Attention To',
  mobile_app_promo: 'Help Genie in your pocket',
  signature: 'Email Signature',
};

// Backwards-compat union (conversation values win on conflict). Prefer
// `getDefaultBlockLabel` so labels are resolved per template type.
export const DEFAULT_BLOCK_LABELS: Record<string, string> = {
  ...CUSTOMER_BLOCK_LABELS,
  ...MEETING_RECAP_BLOCK_LABELS,
  ...ESCALATION_ALERT_BLOCK_LABELS,
  ...CONVERSATION_DIGEST_BLOCK_LABELS,
  ...CONVERSATION_AUTO_DIGEST_BLOCK_LABELS,
  ...VIEW_REPORT_BLOCK_LABELS,
  ...CONVERSATION_BLOCK_LABELS,
} as Record<string, string>;

export function getDefaultBlockLabel(type: ContentBlockType, templateType: EmailTemplateType): string {
  const map =
    templateType === 'conversation_report'
      ? CONVERSATION_BLOCK_LABELS
      : templateType === 'meeting_recap'
        ? MEETING_RECAP_BLOCK_LABELS
        : templateType === 'escalation_alert'
          ? ESCALATION_ALERT_BLOCK_LABELS
          : templateType === 'conversation_digest'
            ? CONVERSATION_DIGEST_BLOCK_LABELS
            : templateType === 'conversation_auto_digest'
              ? CONVERSATION_AUTO_DIGEST_BLOCK_LABELS
              : templateType === 'view_report'
                ? VIEW_REPORT_BLOCK_LABELS
                : CUSTOMER_BLOCK_LABELS;
  return (
    map[type] ||
    DEFAULT_BLOCK_LABELS[type] ||
    type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

// Order mirrors the V1-fallback order in
// supabase/functions/_shared/email/templates/conversation-report-configurable.ts.
export const DEFAULT_CONVERSATION_BLOCKS: ContentBlock[] = [
  { id: 'metrics', type: 'metrics', enabled: true, order: 0 },
  { id: 'top_call_comparison', type: 'top_call_comparison', enabled: true, order: 1 },
  { id: 'goal_funnel', type: 'goal_funnel', enabled: true, order: 2 },
  { id: 'lead_info', type: 'lead_info', enabled: true, order: 3 },
  { id: 'topics', type: 'topics', enabled: true, order: 4 },
  { id: 'insights', type: 'insights', enabled: true, order: 5 },
  { id: 'next_steps', type: 'next_steps', enabled: true, order: 6 },
  { id: 'suggested_follow_up', type: 'suggested_follow_up', enabled: true, order: 7 },
  { id: 'transcript', type: 'transcript', enabled: true, order: 8 },
  { id: 'week_in_review', type: 'week_in_review', enabled: true, order: 9 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 10 },
  { id: 'signature', type: 'signature', enabled: false, order: 11 },
];

export const DEFAULT_CUSTOMER_BLOCKS: ContentBlock[] = [
  { id: 'summary', type: 'summary', enabled: true, order: 0 },
  { id: 'get_more_help', type: 'get_more_help', enabled: true, order: 1 },
  { id: 'topics', type: 'topics', enabled: true, order: 2 },
  { id: 'key_takeaways', type: 'key_takeaways', enabled: true, order: 3 },
  { id: 'highlights', type: 'highlights', enabled: true, order: 4 },
  { id: 'next_steps', type: 'next_steps', enabled: true, order: 5 },
  { id: 'useful_links', type: 'useful_links', enabled: true, order: 6 },
  { id: 'time_saved', type: 'time_saved', enabled: true, order: 7 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 8 },
  { id: 'signature', type: 'signature', enabled: false, order: 9 },
];

// Order mirrors the V1-fallback order in
// supabase/functions/_shared/email/templates/meeting-recap-email-template.ts.
export const DEFAULT_MEETING_RECAP_BLOCKS: ContentBlock[] = [
  { id: 'summary', type: 'summary', enabled: true, order: 0 },
  { id: 'attendees', type: 'attendees', enabled: true, order: 1 },
  { id: 'key_takeaways', type: 'key_takeaways', enabled: true, order: 2 },
  { id: 'key_insights', type: 'key_insights', enabled: true, order: 3 },
  { id: 'decisions', type: 'decisions', enabled: true, order: 4 },
  { id: 'action_plan', type: 'action_plan', enabled: true, order: 5 },
  { id: 'recommendations', type: 'recommendations', enabled: true, order: 6 },
  { id: 'sentiment', type: 'sentiment', enabled: true, order: 7 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 8 },
  { id: 'signature', type: 'signature', enabled: false, order: 9 },
];

// Starts as a near-copy of the conversation report block set (per plan §4b),
// with an urgency banner and escalation reason block prepended. Free to
// diverge from conversation_report further without affecting that template.
export const DEFAULT_ESCALATION_ALERT_BLOCKS: ContentBlock[] = [
  { id: 'urgency_banner', type: 'urgency_banner', enabled: true, order: 0 },
  { id: 'escalation_reason', type: 'escalation_reason', enabled: true, order: 1 },
  { id: 'metrics', type: 'metrics', enabled: true, order: 2 },
  { id: 'lead_info', type: 'lead_info', enabled: true, order: 3 },
  { id: 'topics', type: 'topics', enabled: true, order: 4 },
  { id: 'insights', type: 'insights', enabled: true, order: 5 },
  { id: 'transcript', type: 'transcript', enabled: true, order: 6 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 7 },
  { id: 'signature', type: 'signature', enabled: false, order: 8 },
];

// Genuinely new block set — aggregate period content, not a single-conversation
// transcript/insights layout.
export const DEFAULT_CONVERSATION_DIGEST_BLOCKS: ContentBlock[] = [
  { id: 'digest_summary', type: 'digest_summary', enabled: true, order: 0 },
  { id: 'digest_sentiment_breakdown', type: 'digest_sentiment_breakdown', enabled: true, order: 1 },
  { id: 'digest_topics', type: 'digest_topics', enabled: true, order: 2 },
  { id: 'digest_escalations', type: 'digest_escalations', enabled: true, order: 3 },
  { id: 'digest_conversation_list', type: 'digest_conversation_list', enabled: true, order: 4 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 5 },
  { id: 'signature', type: 'signature', enabled: false, order: 6 },
];

// Genuinely new block set — the AI decision digest groups conversations into
// three categories plus a synthesized recommendations block, rather than one
// flat list like DEFAULT_CONVERSATION_DIGEST_BLOCKS above.
export const DEFAULT_CONVERSATION_AUTO_DIGEST_BLOCKS: ContentBlock[] = [
  { id: 'auto_digest_summary', type: 'auto_digest_summary', enabled: true, order: 0 },
  { id: 'auto_digest_needs_attention', type: 'auto_digest_needs_attention', enabled: true, order: 1 },
  { id: 'auto_digest_recommendations', type: 'auto_digest_recommendations', enabled: true, order: 2 },
  { id: 'auto_digest_auto_handled', type: 'auto_digest_auto_handled', enabled: true, order: 3 },
  { id: 'auto_digest_irrelevant', type: 'auto_digest_irrelevant', enabled: true, order: 4 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 5 },
  { id: 'signature', type: 'signature', enabled: false, order: 6 },
];

// Genuinely new block set — scheduled per-view report: summary + AI takeaways
// + flagged rows, alongside the CSV attachment.
export const DEFAULT_VIEW_REPORT_BLOCKS: ContentBlock[] = [
  { id: 'report_data_summary', type: 'report_data_summary', enabled: true, order: 0 },
  { id: 'report_key_takeaways', type: 'report_key_takeaways', enabled: true, order: 1 },
  { id: 'report_flagged_rows', type: 'report_flagged_rows', enabled: true, order: 2 },
  { id: 'mobile_app_promo', type: 'mobile_app_promo', enabled: true, order: 3 },
  { id: 'signature', type: 'signature', enabled: false, order: 4 },
];

// Value-layer block types — these are auto-injected by the edge function
// renderers when missing from a tenant's saved config. Mirror that here so
// the admin Report Sections panel exposes them too.
const CONVERSATION_VALUE_LAYER_TYPES: ContentBlockType[] = [
  'goal_funnel',
  'next_steps',
  'top_call_comparison',
  'suggested_follow_up',
  'week_in_review',
  'mobile_app_promo',
  'signature',
];

const CUSTOMER_VALUE_LAYER_TYPES: ContentBlockType[] = [
  'get_more_help',
  'topics',
  'key_takeaways',
  'highlights',
  'next_steps',
  'useful_links',
  'time_saved',
  'mobile_app_promo',
  'signature',
];

const MEETING_RECAP_VALUE_LAYER_TYPES: ContentBlockType[] = ['mobile_app_promo', 'signature'];
const ESCALATION_ALERT_VALUE_LAYER_TYPES: ContentBlockType[] = ['mobile_app_promo', 'signature'];
const CONVERSATION_DIGEST_VALUE_LAYER_TYPES: ContentBlockType[] = ['mobile_app_promo', 'signature'];
const CONVERSATION_AUTO_DIGEST_VALUE_LAYER_TYPES: ContentBlockType[] = ['mobile_app_promo', 'signature'];
const VIEW_REPORT_VALUE_LAYER_TYPES: ContentBlockType[] = ['mobile_app_promo', 'signature'];

// Auto-injected block types that must default to OFF when backfilled into an
// existing saved config — unlike the rest of the value layer (which turns on
// immediately for every tenant), the branded signature depends on genie/team
// branding a tenant may not have configured, so it starts hidden until they opt in.
const VALUE_LAYER_DEFAULT_DISABLED: ContentBlockType[] = ['signature'];

// Escalation-defining blocks — a saved config missing these (e.g. the
// agent's shared brand-template row, which carries no type-specific blocks)
// renders as an undifferentiated conversation report with no indication
// it's an escalation. Always led in at the front, ahead of everything else,
// mirroring resolveBlocks in supabase/functions/_shared/email/templates/escalation-alert-configurable.ts.
const ESCALATION_ALERT_LEADING_TYPES: ContentBlockType[] = ['urgency_banner', 'escalation_reason'];

// Block types that were once auto-injected but have since been retired from
// the renderer. Tenants who saved a config while they existed still carry
// them in their stored `blocks` array; strip them out so the Report Sections
// panel doesn't show a dead toggle that no longer renders.
const REMOVED_BLOCK_TYPES: string[] = ['timestamped_highlights', 'rate_call'];

/**
 * Drop any block types that used to exist but have been removed from the
 * renderer, so stale saved configs don't surface dead toggles.
 */
export function stripRemovedBlocks(blocks: ContentBlock[]): ContentBlock[] {
  return blocks.filter((b) => !REMOVED_BLOCK_TYPES.includes(b.type));
}

/**
 * Append any value-layer block types missing from a V2 config's blocks list.
 * Matches the renderer behavior (append at end, enabled by default) so the
 * editor UI shows exactly the blocks that will render. Existing block order
 * is preserved.
 */
export function injectValueLayerBlocks(
  blocks: ContentBlock[],
  templateType: EmailTemplateType,
): ContentBlock[] {
  const valueLayer =
    templateType === 'conversation_report'
      ? CONVERSATION_VALUE_LAYER_TYPES
      : templateType === 'meeting_recap'
        ? MEETING_RECAP_VALUE_LAYER_TYPES
        : templateType === 'escalation_alert'
          ? ESCALATION_ALERT_VALUE_LAYER_TYPES
          : templateType === 'conversation_digest'
            ? CONVERSATION_DIGEST_VALUE_LAYER_TYPES
            : templateType === 'conversation_auto_digest'
              ? CONVERSATION_AUTO_DIGEST_VALUE_LAYER_TYPES
              : templateType === 'view_report'
                ? VIEW_REPORT_VALUE_LAYER_TYPES
                : CUSTOMER_VALUE_LAYER_TYPES;

  let sorted = [...blocks].sort((a, b) => a.order - b.order);
  const existingTypes = new Set(sorted.map((b) => b.type));

  if (templateType === 'escalation_alert') {
    const missingLeading = ESCALATION_ALERT_LEADING_TYPES.filter((type) => !existingTypes.has(type));
    if (missingLeading.length > 0) {
      const leadingBlocks = missingLeading.map((type) => ({ id: type, type, enabled: true, order: 0 }));
      sorted = [...leadingBlocks, ...sorted];
      missingLeading.forEach((type) => existingTypes.add(type));
    }
  }

  let maxOrder = sorted.length > 0 ? Math.max(...sorted.map((b) => b.order)) : -1;

  for (const type of valueLayer) {
    if (existingTypes.has(type)) continue;
    maxOrder += 1;
    sorted.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.includes(type), order: maxOrder });
    existingTypes.add(type);
  }

  sorted = sorted.map((b, i) => ({ ...b, order: i }));

  return pinSignatureLast(sorted);
}

/**
 * The signature depends on genie/team branding and reads as a sign-off — it
 * must always render (and appear in the Report Sections editor) as the final
 * block, regardless of where a saved config's `order` places it — e.g. a
 * config saved before injectValueLayerBlocks pinned this, or a stale/cloned
 * config carrying an out-of-place order value. Reassigns `order` on every
 * block to its array index so BlockEditor's own `.sort((a,b) => a.order -
 * b.order)` reflects this position instead of the original saved values.
 * Mirrors pinSignatureLast in supabase/functions/_shared/email/templates/*.ts,
 * which does the same at send-time (kept in sync by hand — no shared import
 * across the Supabase function boundary).
 */
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === 'signature');
  if (signatureIndex === -1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== 'signature');
  const reordered = [...withoutSignature, blocks[signatureIndex]];
  return reordered.map((b, i) => ({ ...b, order: i }));
}

export function isV2Config(config: EmailTemplateConfig | null | undefined): config is EmailTemplateConfigV2 {
  return config !== null && config !== undefined && (config as EmailTemplateConfigV2).version === 2;
}

export function migrateToV2(config: EmailTemplateConfigV1 | null | undefined, templateType: EmailTemplateType): EmailTemplateConfigV2 {
  const sections = config?.sections;

  const defaultBlocks =
    templateType === 'conversation_report'
      ? DEFAULT_CONVERSATION_BLOCKS
      : templateType === 'meeting_recap'
        ? DEFAULT_MEETING_RECAP_BLOCKS
        : templateType === 'escalation_alert'
          ? DEFAULT_ESCALATION_ALERT_BLOCKS
          : templateType === 'conversation_digest'
            ? DEFAULT_CONVERSATION_DIGEST_BLOCKS
            : templateType === 'conversation_auto_digest'
              ? DEFAULT_CONVERSATION_AUTO_DIGEST_BLOCKS
              : templateType === 'view_report'
                ? DEFAULT_VIEW_REPORT_BLOCKS
                : DEFAULT_CUSTOMER_BLOCKS;

  const blocks: ContentBlock[] = defaultBlocks.map((block) => {
    let enabled = block.enabled;
    if (sections) {
      const sectionKey = BLOCK_TYPE_TO_SECTION_KEY[block.type];
      if (sectionKey && sectionKey in sections) {
        enabled = sections[sectionKey as keyof EmailTemplateSections] !== false;
      }
    }
    return { ...block, enabled };
  });

  return {
    version: 2,
    colors: config?.colors,
    fonts: config?.fonts,
    branding: config?.branding,
    buttons: config?.buttons,
    blocks,
  };
}

const BLOCK_TYPE_TO_SECTION_KEY: Record<string, string> = {
  metrics: 'showMetrics',
  lead_info: 'showLeadInfo',
  topics: 'showTopics',
  insights: 'showInsights',
  transcript: 'showTranscript',
  summary: 'showSummary',
};

// --- Report Data Types ---

export interface ConversationReportData {
  conversation: any;
  aiResult: any;
  topics: any[];
  insights: string[];
  transcript: any[];
  timezone?: string;
}

export interface CustomerReportData {
  conversation: any;
  aiResult: any;
  timezone?: string;
}

export interface EmailTemplateWithAgent {
  id: number;
  user_id: string | null;
  agent_id: string | null;
  agent_name?: string;
  type: EmailTemplateType;
  config: EmailTemplateConfig;
  is_default: boolean;
  created_at: string;
}
